// @ts-nocheck
/**
 * @file Root component of the application. Includes the diagram canvas, toolbars as well as pop-up modals.
 */

import { EdgeSingular, NodeSingular } from 'cytoscape';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { message, Spin } from 'antd';
import { DiagramTreeNode, diagramTreeRoot } from '../model/diagram-tree-model';
import './../css/general.css';
import DiagramCanvas, { cy } from './DiagramCanvas';
import EdgeSelectionModal from './EdgeSelectionModal';
import EdgeTypeSelectionModal from './EdgeTypeSelectionModal';
import LeftSidebar from './LeftSidebar';
import TopToolbar from './TopToolbar';
import TopLeftPanel from './TopLeftPanel';
import TopRightPanel from './TopRightPanel';
import OPLSidebar from './OPLSidebar';
import NodePropertySidebar from './NodePropertySidebar';
import EdgeEditSidebar from './EdgeEditSidebar';
import NodeListPanel from './NodeListPanel';
import ProcessDefinitionModal from './ProcessDefinitionModal';
import SimulationFeature from '../simulation/SimulationFeature';
import type { SimulationSnapshot } from '../simulation/types';
import {
  importPlainJson,
  getPlainExportData,
  resetAllData,
} from '../controller/import-export-plain';
import { masterModelRoot, MMNode } from '../model/node-model';
import {
  derivedEdgeArray,
  MMEdge,
  originalEdgeArray,
} from '../model/edge-model';
import { apiClient } from '@packages/api-client';
import {
  detectDataFormat,
  toPlainFormat,
  isOPMEditorFormat,
} from '../utils/opm-data-adapter';
import { smartFitView } from '../controller/general';
import { DataMapApi } from '../../../api/dataMapApi';
import { useConfig } from '../../../hooks/useConfig';
import {
  isDiagramSwitchingInProgress,
  switchDiagrams,
} from '../controller/diagram-switching';
import { useAppStore } from '../../../store';

export interface useReducerProps {
  state: StateInterface;
  dispatch: Function;
  onNodeSelect?: (node: NodeSingular | null) => void;
  onEdgeSelect?: (edge: EdgeSingular | null) => void;
  onRenderStart?: () => void;
  onRenderEnd?: () => void;
  onDataMap?: (node: NodeSingular) => Promise<void>;
  isDataMapNavigating?: boolean;
  simulationSnapshot?: SimulationSnapshot | null;
}

export interface StateInterface {
  currentDiagram: DiagramTreeNode;
  diagramChangeSource: string | null;
  lastCreatedDiagram: DiagramTreeNode;
  showEdgeTypeSelectonModal: boolean;
  showEdgeSelectionModal: boolean;
  currentNode: NodeSingular | null;
  currentEdge: EdgeSingular | null;
  propagation: Propagation;
  targetNode: null;
  timestamp: Date;
}

export enum PropagationEnum {
  None,
  OneLevel,
  Complete,
}

export let propagation: PropagationEnum = PropagationEnum.None;
export let currentDiagram: DiagramTreeNode = diagramTreeRoot;
// 模块级标志：importPlainJson 初始导入进行中，DiagramCanvas 应跳过 onRenderStart/handlerB 注册
export let isInitialLoading = false;
export const DIAGRAM_CHANGE_SOURCES = {
  INITIAL_IMPORT: 'initial-import',
  RESET_ALL_DATA: 'reset-all-data',
  SIMULATION_NAVIGATION: 'simulation-navigation',
};

// 初始导入/重置空白画布时，图层切换对应的渲染已经由专门流程兜底，不应再次触发切图 loading。
export const shouldSkipDiagramRenderTracking = (source?: string | null) =>
  source === DIAGRAM_CHANGE_SOURCES.INITIAL_IMPORT ||
  source === DIAGRAM_CHANGE_SOURCES.RESET_ALL_DATA ||
  source === DIAGRAM_CHANGE_SOURCES.SIMULATION_NAVIGATION;

export const ACTIONS = {
  CHANGE_DIAGRAM: 'change-diagram',
  INZOOM_DIAGRAM: 'inzoom-diagram',
  EDGE_TYPE_SELECTION: 'edge-type-selection',
  EDGE_SELECTION: 'edge-selection',
  CHANGE_CURRENT_NODE: 'change-current-node',
  CHANGE_CURRENT_EDGE: 'change-current-edge',
  CHANGE_PROPAGATION: 'change-propagation',
  UPDATE_TREE: 'update-tree',
};

// Global state handling
function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.CHANGE_DIAGRAM:
      currentDiagram = action.payload;
      return {
        ...state,
        currentDiagram: action.payload,
        diagramChangeSource: action.source || null,
      };
    case ACTIONS.INZOOM_DIAGRAM:
      currentDiagram = action.payload;
      return {
        ...state,
        currentDiagram: action.payload,
        diagramChangeSource: action.source || null,
        lastCreatedDiagram: action.payload,
      };
    case ACTIONS.EDGE_TYPE_SELECTION:
      return { ...state, showEdgeTypeSelectonModal: action.payload };
    case ACTIONS.EDGE_SELECTION:
      return {
        ...state,
        showEdgeSelectionModal: action.payload.show,
        targetNode: action.payload.node,
      };
    case ACTIONS.CHANGE_PROPAGATION:
      propagation = action.payload;
      return { ...state, propagation: action.payload };
    case ACTIONS.UPDATE_TREE:
      return { ...state, timestamp: new Date() };
    default:
      console.log('invalid dispatch type');
  }
}

const reducerInitState = {
  currentDiagram: diagramTreeRoot,
  diagramChangeSource: null,
  lastCreatedDiagram: null,
  createdEdge: null,
  currentNode: null,
  currentEdge: null,
  propagation: PropagationEnum.None,
};

const OPMCV_PERF_PREFIX = '[OPMCV-PERF]';
const opmcvNow = () =>
  typeof performance !== 'undefined' && performance.now
    ? performance.now()
    : Date.now();
const opmcvPerfEnabled =
  typeof window !== 'undefined' && !!(window as any).__OPMCV_PERF;
const opmcvPerfLog = (...args: any[]) => {
  if (!opmcvPerfEnabled) return;
  console.log(OPMCV_PERF_PREFIX, ...args);
};

interface AppProps {
  debug?: boolean;
  initialData?: string; // JSON 字符串格式的初始数据（Plain 格式）
  ontologyId?: string | null;
  onSave?: (
    plainData: any,
    options?: { silent?: boolean }
  ) => Promise<any> | void; // 保存回调，返回 Plain 格式数据
  isSaving?: boolean; // 是否正在保存
  canEdit?: boolean; // 是否有管理权限（与首页权限管理tab逻辑一致）
}

function App({
  debug = false,
  initialData,
  ontologyId,
  onSave,
  isSaving = false,
  canEdit = true,
}: AppProps) {
  const [state, dispatch] = useReducer(reducer, reducerInitState);
  const navigate = useNavigate();
  const { globalConfig } = useConfig();
  const { setIsEditorDirty, isEditorDirty } = useAppStore();
  const [showOPLSidebar, setShowOPLSidebar] = useState(false);
  const [showNodeEditorSidebar, setShowNodeEditorSidebar] = useState(false);
  const [showEdgeEditorSidebar, setShowEdgeEditorSidebar] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [showProcessDefinitionModal, setShowProcessDefinitionModal] =
    useState(false);
  const [processDefinitionNodeId, setProcessDefinitionNodeId] = useState<
    string | null
  >(null);
  const [isRendering, setIsRendering] = useState(false);
  const [loadingText, setLoadingText] = useState('加载中...');
  const [isDataMapNavigating, setIsDataMapNavigating] = useState(false);
  const showSimulationExecuteButton = !!(globalConfig as any)
    .showSimulationExecuteButton;
  const enableOPMEditorAutoSave = !!(globalConfig as any)
    .enableOPMEditorAutoSave;
  const [simulationResetKey, setSimulationResetKey] = useState(0);
  const ensureVisibleRaf = useRef<number | null>(null);
  const isCenteringRef = useRef(false);
  const ensureVisibleTimeout = useRef<number | null>(null);
  const currentDiagramRef = useRef(state.currentDiagram);
  const isSimulationNavigatingRef = useRef(false);
  const perfSeqRef = useRef(0);
  const loadingPerfRef = useRef<{
    id: number;
    startAt: number;
    reason: string;
  } | null>(null);
  const shouldResetViewAfterInitRef = useRef(false);
  // 标记 importPlainJson 初始导入正在进行，防止 useEffect([currentDiagram]) 竞态重入 isRendering
  const isInitialLoadRef = useRef(false);
  const initialLoadRecoveryTimerRef = useRef<number | null>(null);
  const savePromiseRef = useRef<Promise<any> | null>(null);
  const dirtyVersionRef = useRef(0);
  const skipNextDirtyMarkRef = useRef(false);

  const markEditorDirty = useCallback(() => {
    dirtyVersionRef.current += 1;
    setIsEditorDirty(true);
  }, [setIsEditorDirty]);

  const startLoadingPerf = (reason: string, meta?: any) => {
    // 避免同一轮加载过程中重复覆盖计时（例如：切图触发 + 画布 onRenderStart）
    if (loadingPerfRef.current) {
      opmcvPerfLog(
        `#${loadingPerfRef.current.id}`,
        'loading:start-skip',
        loadingPerfRef.current.reason,
        '->',
        reason
      );
      return;
    }

    const id = (perfSeqRef.current += 1);
    const startAt = opmcvNow();
    loadingPerfRef.current = { id, startAt, reason };
    opmcvPerfLog(`#${id}`, 'loading:start', reason, meta ?? '');

    // 用 rAF 记录“首帧延迟”，如果这里很久才输出，通常说明主线程被同步计算卡住
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        opmcvPerfLog(
          `#${id}`,
          'loading:raf1',
          reason,
          `${(opmcvNow() - startAt).toFixed(1)}ms`
        );
        requestAnimationFrame(() => {
          opmcvPerfLog(
            `#${id}`,
            'loading:raf2',
            reason,
            `${(opmcvNow() - startAt).toFixed(1)}ms`
          );
        });
      });
    }
  };

  const endLoadingPerf = (by: string) => {
    const current = loadingPerfRef.current;
    if (!current) {
      opmcvPerfLog('loading:end(no-session)', by);
      return;
    }
    opmcvPerfLog(
      `#${current.id}`,
      'loading:end',
      current.reason,
      `${(opmcvNow() - current.startAt).toFixed(1)}ms`,
      'by=',
      by
    );
    loadingPerfRef.current = null;
  };

  const completeRenderState = (by: string) => {
    endLoadingPerf(by);
    setIsRendering(false);
    // 初次加载接口数据后：首帧渲染结束自动执行一次视图重置，避免“空画布”错觉
    if (shouldResetViewAfterInitRef.current && cy) {
      shouldResetViewAfterInitRef.current = false;
      requestAnimationFrame(() => smartFitView(cy, 80));
    }
  };

  const finishInitialLoading = (by: string) => {
    if (initialLoadRecoveryTimerRef.current !== null) {
      window.clearTimeout(initialLoadRecoveryTimerRef.current);
      initialLoadRecoveryTimerRef.current = null;
    }
    isInitialLoadRef.current = false;
    completeRenderState(by);
    // 延迟设置 isInitialLoading，确保所有 React 状态更新和渲染完成后
    // 再允许标记 dirty，避免初始数据导入后的副作用触发脏标记
    const rafId = requestAnimationFrame(() => {
      isInitialLoading = false;
    });
    // 存储 rafId 用于清理
    (finishInitialLoading as any).rafId = rafId;
  };

  // 组件挂载时重置初始加载标志
  useEffect(() => {
    isInitialLoading = true;
    isInitialLoadRef.current = true;
    return () => {
      // 清理可能未执行的 requestAnimationFrame
      const rafId = (finishInitialLoading as any).rafId;
      if (rafId) cancelAnimationFrame(rafId);
      // 强制重置初始加载标志，防止残留状态影响下次进入
      isInitialLoading = true;
    };
  }, []);

  // 全局轻提示收敛：只保留最后一条，避免顶部堆叠。
  useEffect(() => {
    message.config({ maxCount: 1 });
  }, []);

  // 监听浏览器 longtask，辅助定位“主线程卡住”的同步计算（仅在支持的浏览器生效）
  useEffect(() => {
    if (!opmcvPerfEnabled) return;

    const PerfObserver: any = (window as any).PerformanceObserver;
    if (!PerfObserver) return;

    let observer: any;
    try {
      observer = new PerfObserver((list: any) => {
        list.getEntries().forEach((entry: any) => {
          opmcvPerfLog(
            'longtask',
            `${Number(entry.duration || 0).toFixed(1)}ms`,
            'start=',
            `${Number(entry.startTime || 0).toFixed(1)}ms`
          );
        });
      });
      observer.observe({ entryTypes: ['longtask'] });
      opmcvPerfLog('longtask:enabled');
    } catch (e) {
      opmcvPerfLog('longtask:failed', e);
    }

    return () => observer?.disconnect?.();
  }, []);

  useEffect(() => {
    currentDiagramRef.current = state.currentDiagram;
  }, [state.currentDiagram]);

  useEffect(() => {
    if (isSimulationNavigatingRef.current) {
      return;
    }
    setSimulationResetKey(prev => prev + 1);
  }, [state.currentDiagram]);

  useEffect(() => {
    return () => {
      if (initialLoadRecoveryTimerRef.current !== null) {
        window.clearTimeout(initialLoadRecoveryTimerRef.current);
        initialLoadRecoveryTimerRef.current = null;
      }
    };
  }, []);

  // 加载初始数据或重置为空白画布
  useEffect(() => {
    if (initialData) {
      dirtyVersionRef.current = 0;
      setIsEditorDirty(false);
      startLoadingPerf('initialData', { size: initialData.length });
      isInitialLoadRef.current = true;
      isInitialLoading = true;
      setIsRendering(true);
      shouldResetViewAfterInitRef.current = true;
      console.log('📥 加载初始数据...');
      try {
        if (initialLoadRecoveryTimerRef.current !== null) {
          window.clearTimeout(initialLoadRecoveryTimerRef.current);
        }
        // 先注册收尾监听，再触发 importPlainJson，避免偶发错过 render 事件导致遮罩无法收敛
        if (cy) {
          cy.one('render', () => {
            requestAnimationFrame(() => {
              finishInitialLoading('onRenderEnd(initialData)');
            });
          });
        }
        // 兜底：若 render 监听因为竞态未命中，也不要让页面永久停留在 loading
        initialLoadRecoveryTimerRef.current = window.setTimeout(() => {
          if (!isInitialLoadRef.current) {
            return;
          }
          opmcvPerfLog('loading:timeout', 'initialData');
          finishInitialLoading('initialData:timeout');
        }, 2500);
        const t0 = opmcvNow();
        opmcvPerfLog('importPlainJson:begin', 'initialData');
        importPlainJson(initialData, dispatch);
        opmcvPerfLog(
          'importPlainJson:end',
          'initialData',
          `${(opmcvNow() - t0).toFixed(1)}ms`
        );
        console.log('✅ 初始数据加载成功');
      } catch (error) {
        console.error('❌ 初始数据加载失败:', error);
        message.error('数据加载失败');
        finishInitialLoading('initialData:error');
      }
    } else {
      // 没有初始数据时，重置所有全局状态，避免上一个本体图的数据残留
      console.log('📋 新建本体图，重置所有数据...');
      dirtyVersionRef.current = 0;
      setIsEditorDirty(false);
      resetAllData(dispatch);
    }
  }, [initialData]);

  // UPDATE_TREE 时（节点/边属性编辑触发），标记为 dirty（跳过初始加载期间）
  useEffect(() => {
    if (skipNextDirtyMarkRef.current) {
      skipNextDirtyMarkRef.current = false;
      return;
    }
    if (state.timestamp && !isInitialLoadRef.current && !isInitialLoading) {
      markEditorDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.timestamp]);

  // 监听画布结构变更（增删节点/边、节点拖动结束），标记为 dirty
  useEffect(() => {
    // 初始导入期间跳过，避免加载数据触发 dirty
    const markDirty = () => {
      if (
        isInitialLoadRef.current ||
        isInitialLoading ||
        isDiagramSwitchingInProgress()
      ) {
        return;
      }
      markEditorDirty();
    };
    // 等待 cy 实例初始化后再挂载事件（cy 是模块级变量，组件挂载后可直接访问）
    let retries = 0;
    let unsubscribe: (() => void) | null = null;
    const tryRegister = () => {
      if (cy) {
        cy.on('add remove dragfree', markDirty);
        unsubscribe = () =>
          cy?.off('add remove dragfree', undefined, markDirty);
      } else if (retries < 30) {
        retries++;
        setTimeout(tryRegister, 100);
      }
    };
    tryRegister();

    return () => {
      unsubscribe?.();
      // 组件卸载时重置 dirty 状态
      dirtyVersionRef.current = 0;
      setIsEditorDirty(false);
    };
  }, [markEditorDirty, setIsEditorDirty]);

  // 保存按钮处理
  const handleSave = useCallback(
    async (options?: { silent?: boolean }) => {
      if (savePromiseRef.current) {
        return savePromiseRef.current;
      }

      const saveTask = (async () => {
        if (!onSave) {
          message.info('保存功能即将上线，敬请期待！');
          return null;
        }

        const saveStartDirtyVersion = dirtyVersionRef.current;

        const formatDateTime = (date: Date) => {
          const pad = (val: number) => String(val).padStart(2, '0');
          return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
            date.getDate()
          )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
            date.getSeconds()
          )}`;
        };

        const bumpVersion = (current?: string) => {
          const parsed = Number(current);
          if (!current || Number.isNaN(parsed)) return '1.0';
          const next = Math.floor(parsed) + 1;
          return `${next}.0`;
        };

        const buildUpdatedByList = (prev: string[] = [], currentName = '') => {
          if (!currentName) return prev;
          const normalized = prev.filter(item => item !== currentName);
          return [currentName, ...normalized];
        };

        const hasSnapshotChanged = (
          node: MMNode,
          snapshot: MMNode['__opmcvEditSnapshot']
        ) => {
          if (!snapshot) return false;
          const tags = Array.isArray(node.tags) ? node.tags : [];
          const snapshotTags = Array.isArray(snapshot.tags)
            ? snapshot.tags
            : [];
          const autonomy = {
            action: node.autonomy?.action || '',
            prompt: node.autonomy?.prompt || '',
            withTool: !!node.autonomy?.withTool,
          };
          const snapshotAutonomy = {
            action: snapshot.autonomy?.action || '',
            prompt: snapshot.autonomy?.prompt || '',
            withTool: !!snapshot.autonomy?.withTool,
          };
          const tagsEqual =
            tags.length === snapshotTags.length &&
            tags.every((item, index) => item === snapshotTags[index]);
          return (
            snapshot.label !== (node.label || '') ||
            snapshot.codeName !== (node.codeName || '') ||
            snapshot.description !== (node.description || '') ||
            snapshot.objectType !== (node.objectType || '') ||
            snapshot.exampleValues !== (node.exampleValues || '') ||
            (snapshot.packageName || '') !== (node.packageName || '') ||
            snapshotAutonomy.action !== autonomy.action ||
            snapshotAutonomy.prompt !== autonomy.prompt ||
            snapshotAutonomy.withTool !== autonomy.withTool ||
            !tagsEqual
          );
        };

        const hasEdgeSnapshotChanged = (
          edge: MMEdge,
          snapshot: MMEdge['__opmcvEditSnapshot']
        ) => {
          if (!snapshot) return false;
          const tags = Array.isArray(edge.tags) ? edge.tags : [];
          const snapshotTags = Array.isArray(snapshot.tags)
            ? snapshot.tags
            : [];
          const tagsEqual =
            tags.length === snapshotTags.length &&
            tags.every((item, index) => item === snapshotTags[index]);
          return (
            snapshot.label !== (edge.label || '') ||
            snapshot.description !== (edge.description || '') ||
            snapshot.instanceFileName !== (edge.instanceFileName || '') ||
            snapshot.instanceFileUrl !== (edge.instanceFileUrl || '') ||
            !tagsEqual
          );
        };

        const currentUser = apiClient.getCurrentUser();
        const currentName = currentUser?.username || '';
        const savedAt = formatDateTime(new Date());
        const changedNodeMeta = new Map<
          string,
          { version: string; updatedAt: string; updatedBy: string[] }
        >();
        const changedEdgeMeta = new Map<
          string,
          {
            version: string;
            updatedAt: string;
            updatedBy: string[];
            label: string;
            tags: string[];
            description: string;
            instanceFileName?: string;
            instanceFileUrl?: string;
            derivedEdgeIds: string[];
          }
        >();

        const collectNodeMetaOnSave = (node: MMNode) => {
          if (!node) return;
          const snapshot = node.__opmcvEditSnapshot;
          const isDirty = node.__opmcvDirty;
          const hasChanged = snapshot
            ? hasSnapshotChanged(node, snapshot)
            : !!isDirty;
          if (
            hasChanged &&
            (node.type === 'object' || node.type === 'process')
          ) {
            changedNodeMeta.set(node.id, {
              version: bumpVersion(node.version),
              updatedAt: savedAt,
              updatedBy: buildUpdatedByList(node.updatedBy || [], currentName),
            });
          }
          if (node.children?.length) {
            node.children.forEach(child => collectNodeMetaOnSave(child));
          }
        };

        const collectEdgeMetaOnSave = (edge: MMEdge) => {
          if (!edge) return;
          const snapshot = edge.__opmcvEditSnapshot;
          const isDirty = edge.__opmcvDirty;
          const hasChanged = snapshot
            ? hasEdgeSnapshotChanged(edge, snapshot)
            : !!isDirty;
          if (hasChanged) {
            changedEdgeMeta.set(edge.id, {
              version: bumpVersion(edge.version),
              updatedAt: savedAt,
              updatedBy: buildUpdatedByList(edge.updatedBy || [], currentName),
              label: edge.label,
              tags: Array.isArray(edge.tags) ? [...edge.tags] : [],
              description: edge.description || '',
              instanceFileName: edge.instanceFileName,
              instanceFileUrl: edge.instanceFileUrl,
              derivedEdgeIds:
                edge.derivedEdges?.map(derived => derived.id) || [],
            });
          }
        };

        const applyEdgeMeta = (edge: any, meta: any) => {
          edge.version = meta.version;
          edge.updatedAt = meta.updatedAt;
          edge.updatedBy = meta.updatedBy;
          edge.label = meta.label;
          edge.tags = [...meta.tags];
          edge.description = meta.description;
          edge.instanceFileName = meta.instanceFileName;
          edge.instanceFileUrl = meta.instanceFileUrl;
        };

        masterModelRoot.children.forEach(child => collectNodeMetaOnSave(child));
        originalEdgeArray.edges.forEach(edge => collectEdgeMetaOnSave(edge));

        const plainData = getPlainExportData();

        if (changedNodeMeta.size > 0) {
          plainData.nodes.forEach(node => {
            const meta = changedNodeMeta.get(node.id);
            if (!meta) return;
            node.version = meta.version;
            node.updatedAt = meta.updatedAt;
            node.updatedBy = meta.updatedBy;
          });
        }

        if (changedEdgeMeta.size > 0) {
          const derivedEdgeMeta = new Map<string, any>();
          plainData.originalEdges.forEach(edge => {
            const meta = changedEdgeMeta.get(edge.id);
            if (!meta) return;
            applyEdgeMeta(edge, meta);
            meta.derivedEdgeIds.forEach(derivedEdgeId => {
              derivedEdgeMeta.set(derivedEdgeId, meta);
            });
          });
          plainData.derivedEdges.forEach(edge => {
            const meta =
              changedEdgeMeta.get(edge.id) || derivedEdgeMeta.get(edge.id);
            if (!meta) return;
            applyEdgeMeta(edge, meta);
          });
        }

        const result = await onSave(plainData, options);
        const hasNewerChanges =
          dirtyVersionRef.current !== saveStartDirtyVersion;

        const applyNodeMetaToModel = (node: MMNode) => {
          if (!node) return;
          const meta = changedNodeMeta.get(node.id);
          if (meta) {
            node.version = meta.version;
            node.updatedAt = meta.updatedAt;
            node.updatedBy = meta.updatedBy;
          }
          if (!hasNewerChanges) {
            node.__opmcvDirty = false;
            node.__opmcvEditSnapshot = undefined;
          }
          if (node.children?.length) {
            node.children.forEach(child => applyNodeMetaToModel(child));
          }
        };

        const applyEdgeMetaToModel = (edge: MMEdge) => {
          if (!edge) return;
          const meta = changedEdgeMeta.get(edge.id);
          if (meta) {
            edge.version = meta.version;
            edge.updatedAt = meta.updatedAt;
            edge.updatedBy = meta.updatedBy;
          }
          if (!hasNewerChanges) {
            edge.__opmcvDirty = false;
            edge.__opmcvEditSnapshot = undefined;
          }
          if (!edge.derivedEdges?.length) return;
          edge.derivedEdges.forEach(derived => {
            if (meta) {
              derived.version = meta.version;
              derived.updatedAt = meta.updatedAt;
              derived.updatedBy = meta.updatedBy;
            }
            derived.label = edge.label;
            derived.tags = Array.isArray(edge.tags) ? [...edge.tags] : [];
            derived.description = edge.description || '';
            derived.instanceFileName = edge.instanceFileName;
            derived.instanceFileUrl = edge.instanceFileUrl;
            if (!hasNewerChanges) {
              derived.__opmcvDirty = false;
              derived.__opmcvEditSnapshot = undefined;
            }
          });
        };

        masterModelRoot.children.forEach(child => applyNodeMetaToModel(child));
        originalEdgeArray.edges.forEach(edge => applyEdgeMetaToModel(edge));
        derivedEdgeArray.edges.forEach(edge => {
          if (!hasNewerChanges) {
            edge.__opmcvDirty = false;
            edge.__opmcvEditSnapshot = undefined;
          }
        });
        skipNextDirtyMarkRef.current = true;
        dispatch({ type: ACTIONS.UPDATE_TREE });
        if (!hasNewerChanges) {
          setIsEditorDirty(false);
        }
        return result;
      })();

      savePromiseRef.current = saveTask;
      saveTask.finally(() => {
        if (savePromiseRef.current === saveTask) {
          savePromiseRef.current = null;
        }
      });
      return saveTask;
    },
    [dispatch, onSave, setIsEditorDirty]
  );

  useEffect(() => {
    if (!onSave || !canEdit || !enableOPMEditorAutoSave) {
      return;
    }

    const timerId = window.setInterval(() => {
      if (isInitialLoadRef.current || isInitialLoading) {
        return;
      }
      if (!isEditorDirty || isDataMapNavigating) {
        return;
      }
      if (savePromiseRef.current || isSaving) {
        return;
      }
      handleSave({ silent: true }).catch(error => {
        console.error('自动保存失败:', error);
      });
    }, 10000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [
    canEdit,
    enableOPMEditorAutoSave,
    handleSave,
    isDataMapNavigating,
    isEditorDirty,
    isSaving,
    onSave,
  ]);

  const handleDataMap = async (node: NodeSingular) => {
    if (!ontologyId) {
      message.warning('未获取到当前本体ID，无法打开数据映射');
      return;
    }
    if (!onSave) {
      message.warning('当前页面不支持保存，无法打开数据映射');
      return;
    }

    if (isDataMapNavigating) {
      return;
    }

    setLoadingText('正在保存并跳转...');
    setIsDataMapNavigating(true);
    setIsRendering(true);

    try {
      await handleSave({ silent: true });
      const structureResponse = await DataMapApi.getStructureObjectByNodeId(
        ontologyId,
        node.id()
      );
      const targetId = structureResponse?.data?.id;

      if (!targetId) {
        throw new Error('未获取到数据映射目标ID');
      }

      const targetUrl = `/data-map-editor?id=${encodeURIComponent(
        String(targetId)
      )}&ontologyId=${encodeURIComponent(ontologyId)}`;
      navigate(targetUrl);
    } catch (error: any) {
      const errorMessage = error?.message || '打开数据映射失败';
      message.error(errorMessage);
    } finally {
      setIsDataMapNavigating(false);
      setIsRendering(false);
    }
  };

  // 显示OPL侧边栏
  const handleShowOPL = () => {
    setShowOPLSidebar(true);
  };

  const handleBeforeSimulationStart = useCallback(() => {
    setShowNodeEditorSidebar(false);
    setShowEdgeEditorSidebar(false);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);

    if (
      typeof window !== 'undefined' &&
      (window as any).__opmcvClearNodeSelection
    ) {
      (window as any).__opmcvClearNodeSelection();
    }
  }, []);

  const handleSimulationNavigateToDiagram = useCallback(
    (targetDiagram: DiagramTreeNode) =>
      new Promise<void>(resolve => {
        if (!cy || !targetDiagram) {
          resolve();
          return;
        }

        const current = currentDiagramRef.current;
        if (!current || current === targetDiagram) {
          resolve();
          return;
        }

        isSimulationNavigatingRef.current = true;

        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          window.setTimeout(() => {
            isSimulationNavigatingRef.current = false;
            resolve();
          }, 32);
        };

        const timeoutId = window.setTimeout(finish, 500);
        cy.one('render', () => {
          window.clearTimeout(timeoutId);
          finish();
        });

        switchDiagrams(current, targetDiagram);
        dispatch({
          type: ACTIONS.CHANGE_DIAGRAM,
          payload: targetDiagram,
          source: DIAGRAM_CHANGE_SOURCES.SIMULATION_NAVIGATION,
        });
      }),
    [dispatch]
  );

  // 关闭OPL侧边栏
  const handleCloseOPL = () => {
    setShowOPLSidebar(false);
  };

  // 选中节点 -> 打开节点属性侧边栏
  const handleSelectNode = (node: NodeSingular | null) => {
    if (node) {
      setSelectedNodeId(node.id());
      setShowNodeEditorSidebar(true);
      setSelectedEdgeId(null);
      setShowEdgeEditorSidebar(false);
    } else {
      setSelectedNodeId(null);
      setShowNodeEditorSidebar(false);
    }
  };

  // 选中边 -> 打开边编辑侧边栏
  const handleSelectEdge = (edge: EdgeSingular | null) => {
    if (edge) {
      setSelectedEdgeId(edge.id());
      setShowEdgeEditorSidebar(true);
      setSelectedNodeId(null);
      setShowNodeEditorSidebar(false);
    } else {
      setSelectedEdgeId(null);
      setShowEdgeEditorSidebar(false);
    }
  };

  // 打开 Process 定义弹窗时，先执行自动保存，再自动收起属性侧边栏并清理节点选中态。
  const handleOpenProcessDefinition = async (nodeId: string) => {
    if (!nodeId) return;

    try {
      if (onSave) {
        await handleSave({ silent: true });
      }
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败，无法打开 Process 定义');
      return;
    }

    setProcessDefinitionNodeId(nodeId);
    setShowProcessDefinitionModal(true);
    setShowNodeEditorSidebar(false);
    if (
      typeof window !== 'undefined' &&
      (window as any).__opmcvClearNodeSelection
    ) {
      (window as any).__opmcvClearNodeSelection();
    }
  };

  const processDefinitionNode = useMemo(() => {
    if (!cy || !processDefinitionNodeId) return null;
    const node = cy.getElementById(processDefinitionNodeId);
    return node && node.length > 0 ? node : null;
  }, [processDefinitionNodeId, state.timestamp]);

  // 侧边栏弹出时，确保已选节点仍在可见区域（避免被侧边栏遮挡）
  useEffect(() => {
    if (!showOPLSidebar || !cy) return;
    const selected = cy.$('node:selected');
    if (selected.length === 0) return;
    const currentZoom = cy.zoom();
    cy.animate(
      {
        center: { eles: selected },
        zoom: currentZoom,
      },
      {
        duration: 200,
        easing: 'ease-in-out',
      }
    );
  }, [showOPLSidebar]);

  // 处理导入（兼容 OPMEditor 和 OPMEditorCV 两种格式）
  const importWithRenderTracking = (
    importJson: string,
    format: 'OPMEditor' | 'OPMEditorCV'
  ) => {
    startLoadingPerf('handleImport', { format });
    isInitialLoadRef.current = true;
    isInitialLoading = true;
    setLoadingText('导入并渲染中...');
    setIsRendering(true);
    shouldResetViewAfterInitRef.current = true;

    if (initialLoadRecoveryTimerRef.current !== null) {
      window.clearTimeout(initialLoadRecoveryTimerRef.current);
    }

    if (cy) {
      cy.one('render', () => {
        requestAnimationFrame(() => {
          finishInitialLoading(`onRenderEnd(handleImport:${format})`);
        });
      });
    }

    initialLoadRecoveryTimerRef.current = window.setTimeout(() => {
      if (!isInitialLoadRef.current) {
        return;
      }
      opmcvPerfLog('loading:timeout', `handleImport:${format}`);
      finishInitialLoading(`handleImport:${format}:timeout`);
    }, 2500);

    const t0 = opmcvNow();
    opmcvPerfLog('importPlainJson:begin', 'handleImport', `format=${format}`);
    importPlainJson(importJson, dispatch);
    opmcvPerfLog(
      'importPlainJson:end',
      'handleImport',
      `format=${format}`,
      `${(opmcvNow() - t0).toFixed(1)}ms`
    );
  };

  const handleImport = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      const format = detectDataFormat(parsed);

      console.log(`📥 检测到导入数据格式: ${format}`);

      if (format === 'OPMEditor') {
        // OPMEditor 老格式，先转换为 Plain 格式再导入
        const plainData = toPlainFormat(parsed);
        if (plainData) {
          importWithRenderTracking(JSON.stringify(plainData), 'OPMEditor');
          console.log('✅ 导入成功（从 OPMEditor 格式转换）');
        } else {
          console.error('转换 OPMEditor 格式失败');
          message.error('导入失败：无法解析文件格式');
        }
      } else if (format === 'OPMEditorCV') {
        // OPMEditorCV Plain 格式，直接导入
        importWithRenderTracking(jsonString, 'OPMEditorCV');
        console.log('✅ 导入成功（OPMEditorCV 格式）');
      } else {
        console.error('未知的数据格式');

        message.error('导入失败：请检查文件内容格式');
        // message.error('导入失败：不支持的文件格式');
      }
    } catch (error) {
      console.error('导入失败:', error);
      if (isInitialLoadRef.current || isInitialLoading) {
        finishInitialLoading('handleImport:error');
      }
      message.error('导入失败：不支持的文件格式');
    }
  };

  // 仅在节点不在可视范围时才定位，带 20px 余量并考虑当前画布宽度（侧边栏让出后）
  const ensureSelectedVisible = useCallback(() => {
    if (!cy || !selectedNodeId) return;
    const selected = cy.getElementById(selectedNodeId) || cy.$('node:selected');
    if (!selected || selected.length === 0) return;

    const extent = cy.extent();
    const padding = 20;
    const bb = selected.boundingBox({ includeLabels: true });

    const fullyVisible =
      bb.x1 >= extent.x1 + padding &&
      bb.x2 <= extent.x2 - padding &&
      bb.y1 >= extent.y1 + padding &&
      bb.y2 <= extent.y2 - padding;
    if (fullyVisible) return;

    // throttle with rAF to避免频繁触发
    if (ensureVisibleRaf.current || isCenteringRef.current) return;
    ensureVisibleRaf.current = requestAnimationFrame(() => {
      ensureVisibleRaf.current = null;
      isCenteringRef.current = true;
      const currentZoom = cy.zoom();
      cy.animate(
        { center: { eles: selected }, zoom: currentZoom },
        {
          duration: 200,
          easing: 'ease-in-out',
          complete: () => {
            // 动画结束后稍作延迟再解除标记，防止立即重复触发
            setTimeout(() => {
              isCenteringRef.current = false;
            }, 50);
          },
        }
      );
    });
  }, [selectedNodeId]);

  // 选中节点或侧边栏变化时，延迟一次可见性检查，避免双重跳动
  useEffect(() => {
    if (!selectedNodeId) return;
    if (ensureVisibleTimeout.current) {
      clearTimeout(ensureVisibleTimeout.current);
      ensureVisibleTimeout.current = null;
    }
    const delay = showOPLSidebar || showNodeEditorSidebar ? 320 : 0;
    ensureVisibleTimeout.current = window.setTimeout(() => {
      ensureVisibleTimeout.current = null;
      ensureSelectedVisible();
    }, delay);
    return () => {
      if (ensureVisibleTimeout.current) {
        clearTimeout(ensureVisibleTimeout.current);
        ensureVisibleTimeout.current = null;
      }
    };
  }, [
    selectedNodeId,
    showOPLSidebar,
    showNodeEditorSidebar,
    ensureSelectedVisible,
  ]);

  // 图层切换时进入渲染态，直到画布反馈渲染完成
  useEffect(() => {
    if (!state.currentDiagram) return;
    if (shouldSkipDiagramRenderTracking(state.diagramChangeSource)) {
      opmcvPerfLog(
        'switchDiagram:skip',
        state.diagramChangeSource,
        state.currentDiagram.label
      );
      return;
    }
    // 初始导入期间跳过：importPlainJson 的 dispatch(CHANGE_DIAGRAM) 会触发此 effect，
    // 但渲染完成已由 useEffect([initialData]) 中注册的 cy.one('render') 处理，
    // 若此处再次 setIsRendering(true) 会导致竞态（handler 注册晚于 cy 渲染 → 永远卡住）
    if (isInitialLoadRef.current) {
      return;
    }
    startLoadingPerf('switchDiagram', { label: state.currentDiagram.label });
    setLoadingText('正在切换图层并渲染...');
    setIsRendering(true);
  }, [state.currentDiagram, state.diagramChangeSource]);

  // 拖动结束时，如果当前展示的节点与最新选中不同，则关闭属性侧边栏
  useEffect(() => {
    if (!showNodeEditorSidebar) return;
    // 暴露给 DiagramCanvas 的回调来调用
    (window as any).__closeEditorIfNotNode = (nodeId: string | null) => {
      if (!nodeId || nodeId !== selectedNodeId) {
        setShowNodeEditorSidebar(false);
      }
    };
    return () => {
      delete (window as any).__closeEditorIfNotNode;
    };
  }, [showNodeEditorSidebar, selectedNodeId]);

  return (
    <div className="app">
      <div className="flex-vertical-wrapper">
        {/* debug 模式下显示顶部工具栏 */}
        {debug && (
          <div className="top-toolbar-wrapper">
            <TopToolbar state={state} dispatch={dispatch} />
          </div>
        )}

        {/* 主内容区域 */}
        <div className="main-content">
          {/* 左侧面板 */}
          <div className={`left-sidebar-wrapper ${!debug ? 'new-layout' : ''}`}>
            {/* 上半部分：本体模型展开图 */}
            <div className="ontology-model-section">
              {!debug && (
                <div className="ontology-panel-header">
                  <h3>本体模型</h3>
                </div>
              )}
              <LeftSidebar state={state} dispatch={dispatch} />
            </div>

            {/* 下半部分：本体组成元素列表 - 仅非 debug 模式显示 */}
            {!debug && (
              <div className="node-list-section">
                <div className="ontology-panel-header">
                  <h3>本体组成元素列表</h3>
                </div>
                <NodeListPanel timestamp={state.timestamp} />
              </div>
            )}
          </div>

          {/* 画布容器 */}
          <div
            className={`canvas-area ${
              !debug && showOPLSidebar ? 'with-opl-sidebar' : ''
            } ${
              !debug && (showNodeEditorSidebar || showEdgeEditorSidebar)
                ? 'with-node-editor-sidebar'
                : ''
            }`}
          >
            <SimulationFeature
              enabled={showSimulationExecuteButton}
              currentDiagram={state.currentDiagram}
              resetKey={simulationResetKey}
              navigateToDiagram={handleSimulationNavigateToDiagram}
              onBeforeStart={handleBeforeSimulationStart}
            >
              {({
                simulationSnapshot,
                simulationStatus,
                onToggleSimulation,
                onResetSimulation,
              }) => (
                <>
                  <div className="diagram-canvas-wrapper">
                    <DiagramCanvas
                      state={state}
                      dispatch={dispatch}
                      simulationSnapshot={simulationSnapshot}
                      onNodeSelect={handleSelectNode}
                      onEdgeSelect={handleSelectEdge}
                      onDataMap={handleDataMap}
                      isDataMapNavigating={isDataMapNavigating}
                      onRenderStart={() => {
                        startLoadingPerf('renderCanvas');
                        setLoadingText('渲染中...');
                        setIsRendering(true);
                      }}
                      onRenderEnd={() => {
                        if (isInitialLoadRef.current || isInitialLoading) {
                          finishInitialLoading('onRenderEnd(renderCanvas)');
                          return;
                        }
                        completeRenderState('onRenderEnd');
                      }}
                    />
                  </div>

                  {/* 仿真插件关闭时，这里拿到的是一组静态空实现，不会初始化 controller。 */}
                  {!debug && (
                    <>
                      {/* 左上角搜索面板 */}
                      <TopLeftPanel />

                      {/* 右上角操作按钮 */}
                      <TopRightPanel
                        onSave={handleSave}
                        onShowOPL={handleShowOPL}
                        onImport={handleImport}
                        onToggleSimulation={onToggleSimulation}
                        onResetSimulation={onResetSimulation}
                        isSaving={isSaving}
                        canEdit={canEdit}
                        simulationStatus={simulationStatus}
                      />
                    </>
                  )}
                </>
              )}
            </SimulationFeature>
          </div>

          {/* 右侧节点属性侧边栏 */}
          {!debug && (
            <NodePropertySidebar
              visible={showNodeEditorSidebar}
              selectedNodeId={selectedNodeId}
              onClose={() => setShowNodeEditorSidebar(false)}
              dispatch={dispatch}
              onOpenProcessDefinition={handleOpenProcessDefinition}
              offsetRight={0}
            />
          )}

          {/* 右侧边编辑侧边栏 */}
          {!debug && (
            <EdgeEditSidebar
              visible={showEdgeEditorSidebar}
              selectedEdgeId={selectedEdgeId}
              onClose={() => {
                setShowEdgeEditorSidebar(false);
                setSelectedEdgeId(null);
              }}
              offsetRight={0}
            />
          )}

          {/* OPL 语言侧边栏 - 仅在非 debug 模式下显示 */}
          {!debug && (
            <OPLSidebar
              visible={showOPLSidebar}
              onClose={handleCloseOPL}
              offsetRight={
                showNodeEditorSidebar || showEdgeEditorSidebar ? 260 : 0
              }
            />
          )}
        </div>
      </div>
      <EdgeTypeSelectionModal state={state} dispatch={dispatch} />
      <EdgeSelectionModal state={state} dispatch={dispatch} />
      <ProcessDefinitionModal
        visible={showProcessDefinitionModal}
        node={processDefinitionNode}
        ontologyId={ontologyId ? parseInt(ontologyId, 10) || 1 : 1}
        onClose={() => {
          setShowProcessDefinitionModal(false);
          setProcessDefinitionNodeId(null);
        }}
      />

      {isRendering && (
        <div
          style={{
            position: 'fixed',
            height: '100vh',
            inset: 0,
            background: 'rgba(255,255,255,0.55)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '500px',
          }}
        >
          {/* antd Spin 的 tip 仅在嵌套子元素或 fullscreen 模式下生效，这里用自定义文案避免控制台警告 */}
          <Spin size="large" />
          {/* <div style={{ marginTop: 12, color: 'rgba(0,0,0,0.65)' }}>
            {loadingText}
          </div> */}
        </div>
      )}
    </div>
  );
}

export default App;
